import {
  Table as MuiTable,
  TableCell as MuiTableCell,
  TableRow as MuiTableRow,
  TableContainer as MuiTableContainer,
  Box
} from '@mui/material'
import { css } from '@emotion/react'
import styled from '@emotion/styled'

export interface StyledProps {
  columnQuantity: number
  align?: 'left' | 'center' | 'right'
  height?: number
  isNegative?: boolean
  minWidth?: number | string
  maxWidth?: number | string
  sticky?: boolean
  stickyPosition?: 'left' | 'right'
  stickyOffset?: number
  hasScroll?: boolean
  scrollLeft?: number
  scrollRight?: number
  columnColor?: string
  fontWeight?: number
}

export const Container = styled(Box)`
  ${({ theme }) => css`
    border: 1px solid ${theme.colors.border.light};
    border-radius: 10px;
    overflow: auto;
  `}
`

export const Header = styled(Box)`
  ${({ theme }) => css`
    height: 48px;
    border-radius: 10px 10px 0 0;
    padding: 10px 12px;
    background: ${theme.colors.primary};
    border: 1px solid ${theme.colors.primary};
    display: flex;
    align-items: center;
    justify-content: space-between;
  `}
`

export const HeaderTitle = styled(Box)`
  ${({ theme }) => css`
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 130%;
    color: ${theme.colors.lightest};
    display: flex;
    align-items: center;
    gap: 24px;
    cursor: pointer;
  `}
`

export const HeaderPeriod = styled(Box)`
  ${({ theme }) => css`
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 130%;
    color: ${theme.colors.lightest};
    display: flex;
    align-items: center;
    gap: 24px;
    cursor: pointer;

    @media (max-width: ${theme.screenSizes.tablet}) {
      max-width: 180px;
      font-size: 12px;
    }
  `}
`

export const TableWrapper = styled(Box)`
  position: relative;
`

export const TableContent = styled(MuiTableContainer)`
  ${({ theme }) => css`
    position: relative;
    overflow-x: auto;
    overflow-y: auto;

    &::-webkit-scrollbar {
      height: 36px;
      border-top: 1px solid ${theme.colors.border.light};
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${theme.colors.gray.scrollbar};
      border-radius: 20px;
      border: 10px solid transparent;
      background-clip: content-box;
      cursor: pointer;
    }

    scrollbar-width: auto;
    scrollbar-color: ${theme.colors.secondary};
  `}
`

export const Table = styled(MuiTable)``

export const HeadCell = styled(MuiTableCell)<StyledProps>`
  ${({ theme, align, minWidth, maxWidth, sticky, stickyPosition, stickyOffset, columnColor }) => css`
    text-align: ${align ?? 'left'};
    min-width: ${typeof minWidth === 'number' ? `${minWidth}px` : (minWidth ?? 'auto')};
    max-width: ${typeof maxWidth === 'number' ? `${maxWidth}px` : (maxWidth ?? 'none')};
    width: ${minWidth === '5rem' ? '5rem' : 'auto'};
    flex-shrink: ${minWidth === '5rem' ? '0' : '1'};

    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
    color: ${theme.colors.blue.dark};
    background: ${columnColor || theme.colors.blue.head};

    padding: 0 12px;
    border-bottom: none;

    &:not(:last-child) {
      border-right: 1px solid ${theme.colors.border.light};
    }

    ${sticky &&
    css`
      position: sticky;
      top: 0;
      z-index: 2;
      ${stickyPosition === 'left' && `left: ${stickyOffset || 0}px;`}
      ${stickyPosition === 'right' && `right: ${stickyOffset || 0}px;`}

      &.MuiTableCell-head {
        z-index: 3;
      }

      &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 1px;
        height: 100%;
        z-index: 1;
        background: ${theme.colors.border.light};
      }
    `}
  `}
`

export const TableCell = styled(HeadCell)<StyledProps>`
  ${({ theme, isNegative, columnColor, fontWeight, minWidth }) => css`
    font-weight: ${fontWeight || 400};
    background: ${columnColor || theme.colors.lightest};
    border-top: 1px solid ${theme.colors.border.light};
    color: ${isNegative ? theme.colors.error.color : theme.colors.secondary};
    width: ${minWidth === '5rem' ? '5rem' : 'auto'};
    flex-shrink: ${minWidth === '5rem' ? '0' : '1'};
  `}
`

export const TableCellValue = styled(Box)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const TableRow = styled(MuiTableRow)`
  height: 48px;
`

export const TableInput = styled(Box)`
  ${({ theme }) => css`
    width: 100%;
    outline: none;

    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 130%;
    color: ${theme.colors.secondary};
  `}
`

export const ResizingIcon = styled(Box)`
  position: absolute;
  right: 0;
  top: 10px;
  height: 30px;
  width: 2px;
  z-index: 20;
  cursor: col-resize;
  background-color: rgba(0, 0, 0, 0.1);
`

export const Footer = styled(Box)`
  ${({ theme }) => css`
    min-height: 68px;
    border-radius: 0 0 10px 10px;
    padding: 10px;
    border-top: 1px solid ${theme.colors.border.light};
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media (max-width: ${theme.screenSizes.laptop}) {
      flex-direction: column-reverse;
      align-items: center;
      justify-content: flex-end;
      gap: 16px;
    }
  `}
`

export const FooterActions = styled(Box)`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 8px;

    @media (max-width: ${theme.screenSizes.laptop}) {
      width: 100%;
      flex-direction: column;
    }
  `}
`

export const AditionalRow = styled(Box)`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: ${theme.colors.gray.footer};
    border-top: 1px solid ${theme.colors.border.light};
  `}
`

export const GridMessage = styled(Box)`
  min-height: 152px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`

export const EmptyMessage = styled(Box)`
  ${({ theme }) => css`
    color: ${theme.colors.secondary};
    font-size: 16px;
    line-height: 16px;
  `}
`

export const LoaderBox = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  min-height: 145px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.63);
  z-index: 10;
  pointer-events: none;
  box-sizing: border-box;
`
